import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Wrench, LineChart, ArrowRight } from 'lucide-react';
import EnergyControlCTAModal from './EnergyControlCTAModal';
import { ENERGY_CONTROL_MONTHLY_PRICE_EUR, formatEur } from '../../config/pricing';
import { trackFunnelEvent } from '@/utils/tracking';

const CARD_CTA_META = {
  start_package: { cta_type: 'starter_kit', destination: '/energy-control-starter-kit' },
  installation: { cta_type: 'installation_quote', destination: 'modal' },
  monitoring: { cta_type: 'monitoring_interest', destination: 'modal' },
};

const CARDS = [
  {
    type: 'start_package',
    icon: Package,
    title: 'Stel mijn startpakket samen',
    body: 'Ontvang een concreet startpakket op basis van uw woning, grootverbruikers en gewenste mate van automatisering.',
    button: 'Startpakket samenstellen',
    track: 'energy_control_start_package_click',
    accent: 'primary',
  },
  {
    type: 'installation',
    icon: Wrench,
    title: 'Vraag veilige installatie aan',
    body:
      'Voor groepenkast, boiler, warmtepomp, laadpaal of zware verbruikers is veilige installatie belangrijk. Laat een geschikte installateur meekijken.',
    button: 'Installatie aanvragen',
    track: 'energy_control_installation_click',
    accent: 'primary',
  },
  {
    type: 'monitoring',
    icon: LineChart,
    title: 'Activeer optionele maandelijkse monitoring',
    body: 'Blijf elke maand zien of uw woning uw eigen stroom slim genoeg gebruikt.',
    sub: `Optioneel na uw eenmalige Energy Control Plan. Richtprijs: ${formatEur(ENERGY_CONTROL_MONTHLY_PRICE_EUR)} per maand.`,
    button: 'Monitoring interesse tonen',
    track: 'energy_control_monitoring_click',
    accent: 'soft',
  },
];

export default function EnergyControlCTAs({ intakeId }) {
  const navigate = useNavigate();
  const [openType, setOpenType] = useState(null);

  const handleClick = (card) => {
    if (window.pwTrack) window.pwTrack(card.track, { intake_id: intakeId });
    // Macro Funnel Completion Sprint (Feb 2026) — canonical report CTA event,
    // distinct per cta_type so analytics can split Starter Kit / installation /
    // monitoring interest without parsing free-form labels.
    const ctaMeta = CARD_CTA_META[card.type] || {};
    trackFunnelEvent('report_cta_clicked', {
      intake_id: intakeId,
      meta: {
        cta_type: ctaMeta.cta_type || card.type,
        cta_label: card.button || card.title,
        report_type: 'energy_control',
        destination: ctaMeta.destination || 'unknown',
      },
    });
    if (card.type === 'start_package') {
      // Phase 5: dedicated starter-kit page (compatibility check + order form)
      navigate(intakeId ? `/energy-control-starter-kit/${intakeId}` : '/energy-control-starter-kit/new');
      return;
    }
    setOpenType(card.type);
  };

  return (
    <div className="container mx-auto px-6 pb-16" data-testid="ec-ctas">
      <div className="max-w-2xl mx-auto">
        <div className="mb-5">
          <div className="text-xs uppercase tracking-wider text-primary/70 font-medium mb-1">
            Volgende stap
          </div>
          <h2 className="text-xl sm:text-2xl font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
            Wat wilt u nu doen?
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {CARDS.map((card, idx) => {
            const Icon = card.icon;
            const isMonitoring = card.type === 'monitoring';
            return (
              <div
                key={card.type}
                className={`relative bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-border flex flex-col ${
                  isMonitoring ? 'sm:col-span-2' : ''
                }`}
                data-testid={`ec-cta-card-${card.type}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-primary/60 mb-1">
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <h3
                      className="text-base sm:text-lg font-medium text-primary leading-tight"
                      style={{ fontFamily: 'Outfit' }}
                      data-testid={`ec-cta-title-${card.type}`}
                    >
                      {card.title}
                    </h3>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {card.body}
                </p>
                {card.sub && (
                  <p className="text-xs text-muted-foreground italic mt-2 leading-relaxed">
                    {card.sub}
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => handleClick(card)}
                  className="btn-primary mt-4 w-full sm:w-auto self-start inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium"
                  data-testid={`ec-cta-button-${card.type}`}
                >
                  {card.button}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-5 text-center">
          Alle aanvragen zijn vrijblijvend · u zit nergens aan vast · geen automatische afschrijving.
        </p>
      </div>

      <EnergyControlCTAModal
        open={openType !== null}
        onClose={() => setOpenType(null)}
        ctaType={openType}
        intakeId={intakeId}
      />
    </div>
  );
}
